import { useEffect, useState } from 'react'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import type { InviteDoc, MemberDoc } from '@racedash/shared'
import { db } from '../lib/firebase'

export interface Member extends MemberDoc {
  id: string
}
export interface Invite extends InviteDoc {
  token: string
}

/** Members of an organisation. Readable by any member — people should be able
 *  to see who else is in the room. */
export function useMembers(orgId: string | undefined) {
  const [members, setMembers] = useState<Member[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!orgId) return
    return onSnapshot(
      collection(db, 'orgs', orgId, 'members'),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as MemberDoc) }))
        const rank = { owner: 0, editor: 1, viewer: 2 } as const
        list.sort(
          (a, b) =>
            rank[a.role] - rank[b.role] ||
            (a.displayName ?? '').localeCompare(b.displayName ?? ''),
        )
        setMembers(list)
      },
      (e) => setError(e.message),
    )
  }, [orgId])

  return { members, error }
}

/** Outstanding invites. Owners only — the rules refuse the list to anyone else,
 *  since the document id is the join token. */
export function usePendingInvites(orgId: string | undefined, enabled: boolean) {
  const [invites, setInvites] = useState<Invite[] | null>(null)

  useEffect(() => {
    if (!orgId || !enabled) {
      setInvites(null)
      return
    }
    return onSnapshot(
      query(collection(db, 'invites'), where('orgId', '==', orgId)),
      (snap) =>
        setInvites(
          snap.docs
            .map((d) => ({ token: d.id, ...(d.data() as InviteDoc) }))
            .filter((i) => !i.acceptedBy),
        ),
      () => setInvites([]),
    )
  }, [orgId, enabled])

  return invites
}
